import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { StatTile } from '@/components/StatTile';
import { formatCompact } from '@/components/TrendChart';
import { formatPercent } from '@/components/viz';

/**
 * Feature adoption across every tenant — the question a control plane exists to answer.
 *
 * A feature is the `(app_key, target_type)` pair: the app and the thing acted on. Per-account
 * usage lives on the account page; this is the platform view, and the two columns that matter are
 * **% of tenants** (is this feature discovered at all) and the **never used** list (what are we
 * maintaining for nobody).
 */
export function FeaturesPage() {
  // Internal environments are excluded by default. Most tenants on this platform ARE environments,
  // so including them inflates the denominator of every share several times over.
  const [includeInternal, setIncludeInternal] = useState(false);

  const adoption = useQuery({
    queryKey: ['platform-features', includeInternal],
    queryFn: () => api.telemetry.platformFeatures(includeInternal),
  });

  const unused = useQuery({
    queryKey: ['platform-features-unused'],
    queryFn: () => api.telemetry.unusedFeatures(),
  });

  if (adoption.isLoading) {
    return <div className="spinner" aria-label="Loading feature adoption" />;
  }

  if (adoption.isError) {
    return (
      <Callout tone="error" title="Could not load feature adoption">
        {adoption.error instanceof Error ? adoption.error.message : 'Unknown error.'}
      </Callout>
    );
  }

  const data = adoption.data;
  if (!data) return null;

  const unknown = data.features.filter((f) => !f.is_known);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="t-display" style={{ margin: 0 }}>Feature adoption</h1>
          <p className="t-body muted" style={{ margin: '4px 0 0' }}>
            {data.from} to {data.to} · across {data.tenants_in_scope}{' '}
            {data.includes_internal ? 'accounts including internal environments' : 'customer accounts'}
          </p>
        </div>
        <button
          type="button"
          className={`btn ${includeInternal ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setIncludeInternal((v) => !v)}
        >
          {includeInternal ? 'Including internal' : 'Customers only'}
        </button>
      </div>

      {data.tenants_in_scope === 0 && (
        <Callout tone="warning" title="No accounts in scope">
          Every share below would have no denominator. Turn on internal environments, or import
          accounts first.
        </Callout>
      )}

      <div className="kpi-row">
        <StatTile label="Features in use" value={data.features.length} />
        <StatTile
          label="Accounts in scope"
          value={data.tenants_in_scope}
          sub={data.includes_internal ? 'incl. internal' : 'customers only'}
        />
        <StatTile
          label="Unrecognised"
          value={unknown.length}
          sub={unknown.length > 0 ? 'not in the registry' : 'all features named'}
          tone={unknown.length > 0 ? 'warning' : 'default'}
        />
        <StatTile
          label="Never used"
          value={unused.data?.length ?? null}
          sub="registered, zero accounts"
          tone={(unused.data?.length ?? 0) > 0 ? 'warning' : 'default'}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Most used, by events</h2>
            <span className="t-label subtle">all accounts in scope</span>
          </div>
          <div className="card-body">
            <BarList
              rows={data.features.map((f) => ({ label: f.display_name, value: f.event_count }))}
              limit={10}
              emptyLabel="No feature usage collected yet."
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Most widely adopted</h2>
            <span className="t-label subtle">by number of accounts</span>
          </div>
          <div className="card-body">
            {/* Ranked by REACH, not volume. A feature one account hammers and a feature every
                account touches are different findings, and only this one answers "did it land". */}
            <BarList
              rows={[...data.features]
                .sort((a, b) => b.tenants_using - a.tenants_using)
                .map((f) => ({ label: f.display_name, value: f.tenants_using }))}
              limit={10}
              emptyLabel="No feature usage collected yet."
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Every feature</h2>
          <span className="t-label subtle">
            {data.newest_sync ? `replica synced ${new Date(data.newest_sync).toLocaleString()}` : 'never synced'}
          </span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>App</th>
                <th className="num">Events</th>
                <th className="num">Accounts</th>
                <th className="num">% of accounts</th>
              </tr>
            </thead>
            <tbody>
              {data.features.map((f) => (
                <tr key={`${f.app_key}:${f.target_type}`}>
                  <td>
                    <span style={{ fontWeight: 600 }}>{f.display_name}</span>
                    {/* Flagged, not hidden: an unrecognised feature usually means a service is
                        emitting a vocabulary nothing else uses, which is the finding. */}
                    {!f.is_known && <span className="badge-muted">unrecognised</span>}
                    <div className="t-caption muted mono">{f.target_type}</div>
                  </td>
                  <td className="muted">{f.app_key}</td>
                  <td className="num">{formatCompact(f.event_count)}</td>
                  <td className="num">{f.tenants_using}</td>
                  {/* formatPercent dashes on null, so an unmeasurable share never reads as 0%. */}
                  <td className="num">{formatPercent(f.tenant_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(unused.data?.length ?? 0) > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Never used by anyone</h2>
            <span className="t-label subtle">registered, zero accounts</span>
          </div>
          <div className="card-body">
            <p className="t-body muted" style={{ marginTop: 0 }}>
              These have been observed at least once but no account in the replica currently uses
              them — undiscoverable, unwanted, or broken. A feature only appears here once it has
              fired at least once, so this is not a list of everything the platform could do.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {unused.data!.map((f) => (
                <span key={`${f.app_key}:${f.target_type}`} className="chip chip-off">
                  {f.display_name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
